// src/sim/shields.ts -- pure, no DOM

export interface ShieldDef {
  id: string;
  name: string;
  cost: number;
  qty: number;
  energy: number;
  deflects: boolean;
  absorbs: boolean;
}

export const SHIELDS: readonly ShieldDef[] = [
  { id: 'mag-deflector', name: 'Mag Deflector', cost: 10000, qty: 2, energy: 500, deflects: true, absorbs: false },
  { id: 'shield', name: 'Shield', cost: 20000, qty: 3, energy: 1000, deflects: false, absorbs: true },
  { id: 'force-shield', name: 'Force Shield', cost: 25000, qty: 3, energy: 1500, deflects: true, absorbs: true },
  { id: 'heavy-shield', name: 'Heavy Shield', cost: 30000, qty: 2, energy: 2500, deflects: false, absorbs: true },
  { id: 'super-mag', name: 'Super Mag', cost: 40000, qty: 2, energy: 3000, deflects: true, absorbs: true },
];

export const SHIELDS_BY_ID: Record<string, ShieldDef> = SHIELDS.reduce(
  (map, s) => { map[s.id] = s; return map; },
  {} as Record<string, ShieldDef>,
);

/**
 * Apply damage to a tank considering its active shield.
 * Shield absorbs damage from its energy pool.
 * Burial damage (isDirtBurial=true) bypasses shields entirely.
 * Returns actual HP damage dealt.
 */
export function applyShieldedDamage(
  tank: { hp: number; alive: boolean; flashTimer: number; activeShield: { defId: string; energy: number } | null },
  rawDamage: number,
  isDirtBurial: boolean,
): number {
  if (rawDamage <= 0) return 0;

  // Burial damage bypasses shields entirely (TNK-06)
  if (isDirtBurial) {
    tank.hp -= rawDamage;
    if (tank.hp <= 0) tank.alive = false;
    return rawDamage;
  }

  // No active shield -> full damage to HP
  if (!tank.activeShield) {
    tank.hp -= rawDamage;
    tank.flashTimer = 0.15;
    if (tank.hp <= 0) tank.alive = false;
    return rawDamage;
  }

  // Shield absorbs damage from energy pool
  const shield = tank.activeShield;
  if (shield.energy >= rawDamage) {
    // Shield absorbs all damage
    shield.energy -= rawDamage;
    tank.flashTimer = 0.15;
    if (shield.energy <= 0) {
      tank.activeShield = null; // shield breaks
    }
    return 0;
  } else {
    // Shield absorbs partial, rest goes to HP
    const absorbed = shield.energy;
    const remainder = rawDamage - absorbed;
    shield.energy = 0;
    tank.activeShield = null; // shield breaks
    tank.hp -= remainder;
    tank.flashTimer = 0.15;
    if (tank.hp <= 0) tank.alive = false;
    return remainder;
  }
}
